/**
 * @fileoverview Provide shared event presentation and action signifiers across calendar views. The caller supplies event/current-user context; responsibility confirmation and proposal decisions return through callbacks. `kc-internal-frontend/src/components/Calendar/utils/calendarUtils.js` owns timing/layout semantics. Displayed assignee matching is a UI affordance; backend authorization still governs accepted actions.
 */

import React, { useState, useRef } from 'react';
import { formatTime, formatDate } from '../utils/calendarUtils';
import moment from 'moment-timezone';
import AppIcon from "../../shared/AppIcon";



const EventComponent = ({
  event,
  style = {},
  viewType,
  onEventClick,
  onEventEdit,
  onEventDelete,
  onConfirmResponsibility,
  assignees = [],
  isCompact = false,
  isDraggable = false,
  onDragStart,
  onDragEnd,
  currentUser = null
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const eventRef = useRef(null);




  // Get assignee name for display
  const getAssigneeNames = () => {
    if (event.assignees && event.assignees.length > 0) {
      return event.assignees.map((a) => a.attorney_name).join(', ');
    }
    return '';
  };



  const renderAssigneeAvatars = () => {
    // AI events get a robot emoji instead of avatars
    if (event.source === 'ai-calendar') {
      return (
        <div className="event-assignee-avatars">
          <div
            title="AI-Detected Deadline"
            style={{
              width: isCompact ? '16px' : '20px',
              height: isCompact ? '16px' : '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isCompact ? '12px' : '16px',
              flexShrink: 0
            }}>

            🤖
          </div>
        </div>);

    }

    if (!event.assignees || event.assignees.length === 0) {
      return null;
    }

    const renderAvatar =

    (assignee, index) => {
      const initials = assignee.attorney_name ?
      assignee.attorney_name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() :
      '??';

      const avatarSize = isCompact ? '16px' : '20px';
      const fontSize = isCompact ? '7px' : '8px';

      const avatarStyle = {
        width: avatarSize,
        height: avatarSize,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.5)',
        objectFit: 'cover',
        flexShrink: 0
      };

      const initialsStyle = {
        ...avatarStyle,
        backgroundColor: 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: fontSize,
        fontWeight: 'bold'
      };

      // Check if profile picture exists and is a valid URL
      const hasProfilePic = assignee.attorney_profile_pic &&
      typeof assignee.attorney_profile_pic === 'string' &&
      assignee.attorney_profile_pic.trim() !== '';

      if (hasProfilePic) {
        return (
          <img
            key={`avatar-${assignee.attorney_id}-${index}`}
            src={assignee.attorney_profile_pic}
            alt={assignee.attorney_name}
            title={assignee.attorney_name}
            style={avatarStyle}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={

            (e) => {
              // Gracefully fall back to initials if profile picture fails to load
              const parent = e.target.parentNode;
              const initialsDiv = document.createElement('div');
              initialsDiv.textContent = initials;
              initialsDiv.title = assignee.attorney_name;
              Object.assign(initialsDiv.style, initialsStyle);
              parent.replaceChild(initialsDiv, e.target);
            }} />);


      } else {
        return (
          <div
            key={`avatar-${assignee.attorney_id}-${index}`}
            title={assignee.attorney_name}
            style={initialsStyle}>

            {initials}
          </div>);

      }
    };

    return (
      <div className="event-assignee-avatars">
        {event.assignees.slice(0, 3).map((assignee, index) => renderAvatar(assignee, index))}
        {event.assignees.length > 3 &&
        <div
          title={event.assignees.slice(3).map((a) => a.attorney_name).join(', ')}
          style={{
            width: isCompact ? '16px' : '20px',
            height: isCompact ? '16px' : '20px',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.5)',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isCompact ? '7px' : '8px',
            fontWeight: 'bold',
            flexShrink: 0
          }}>

            +{event.assignees.length - 3}
          </div>}

      </div>);

  };



  const isUserAssignee = () => {
    return !!(currentUser && Array.isArray(event.assignees) &&
    event.assignees.some((a) => a.attorney_name === currentUser.Name));
  };

  // Get event styles based on category and confirmation status
  const getEventStyles =

  () => {
    const baseStyle = {
      position: 'absolute',
      cursor: 'pointer',
      borderRadius: '4px',
      padding: isCompact ? '1px 3px' : '4px 8px',
      fontSize: isCompact ? '0.7rem' : '0.85rem',
      lineHeight: isCompact ? 1.1 : 1.2,
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: isCompact ? 'nowrap' : 'normal',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      border: '1px solid rgba(255,255,255,0.2)',
      transition: 'all 0.2s ease',
      zIndex: isDragging ? 1000 : 10,
      opacity: isDragging ? 0.7 : 1,
      ...style
    };

    // Determine system theme (prefers dark) and pick appropriate palette
    const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches ||
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');

    // Apply category colors from the event's calendar configuration
    if (event.calendarId && window.calendarColors && window.calendarColors[event.calendarId]) {
      const colorConfig = window.calendarColors[event.calendarId];
      const palette = prefersDark ? colorConfig.darkColors : colorConfig.lightColors;
      baseStyle.backgroundColor = palette.container;
      baseStyle.color = palette.onContainer;
      baseStyle.borderLeftColor = palette.main;
      baseStyle.borderLeftWidth = '4px';
      baseStyle.borderLeftStyle = 'solid';
    } else {
      // Fallback colors
      if (prefersDark) {
        baseStyle.backgroundColor = 'var(--surface-section)';
        baseStyle.color = 'var(--text-color)';
        baseStyle.borderLeftColor = 'var(--primary-color)';
      } else {
        baseStyle.backgroundColor = '#e3f2fd';
        baseStyle.color = '#1565c0';
        baseStyle.borderLeftColor = '#1976d2';
      }
      baseStyle.borderLeftWidth = '4px';
      baseStyle.borderLeftStyle = 'solid';
    }

    // Apply event type specific styling
    if (event.type === 'hard-deadline') {
      baseStyle.borderLeftColor = prefersDark ? 'var(--red-400, #f87171)' : '#f44336';
      baseStyle.backgroundColor = prefersDark ?
      'color-mix(in srgb, var(--red-500, #ef4444) 18%, transparent)' :
      '#ffebee';
      baseStyle.color = prefersDark ? 'var(--surface-900, #ffffff)' : '#c62828';
    } else if (event.type === 'soft-deadline') {
      baseStyle.borderLeftColor = prefersDark ? 'var(--orange-400, #fb923c)' : '#ff9800';
      baseStyle.backgroundColor = prefersDark ?
      'color-mix(in srgb, var(--orange-500, #f97316) 18%, transparent)' :
      '#fff3e0';
      baseStyle.color = prefersDark ? 'var(--surface-900, #ffffff)' : '#e65100';
    }

    // Unconfirmed events get special styling
    if (!event.confirmed) {
      baseStyle.opacity = 0.7;
      baseStyle.borderStyle = 'dashed';
    }



    if (event.source === 'ai-calendar' && event.ai_status === 'PENDING_APPROVAL') {
      baseStyle.opacity = 0.8;
      baseStyle.borderStyle = 'dashed';
      baseStyle.borderLeftColor = '#ff9800'; // Orange to indicate pending action
      baseStyle.borderLeftWidth = '4px';
      baseStyle.borderLeftStyle = 'solid';
    }

    return baseStyle;
  };

  // Handle HTML5 drag start
  const handleDragStart =

  (e) => {
    if (!isDraggable) {
      e.preventDefault();
      return;
    }

    setIsDragging(true);

    // Set up drag data for HTML5 drag and drop
    if (e.dataTransfer) {
      e.dataTransfer.setData('application/json', JSON.stringify(event));
      e.dataTransfer.effectAllowed = 'move';
    }

    if (onDragStart) {
      onDragStart(event, e);
    }
  };

  // Handle drag end
  const handleDragEnd =

  (e) => {
    setIsDragging(false);

    if (onDragEnd) {
      onDragEnd(event, {}, e);
    }
  };

  const handleEventClick =

  (e) => {
    e.stopPropagation();
    if (onEventClick) {
      onEventClick(event, e);
    }
  };

  const handleEditClick =

  (e) => {
    e.stopPropagation();
    if (onEventEdit) {
      onEventEdit(event);
    }
  };

  const handleDeleteClick =

  (e) => {
    e.stopPropagation();
    if (onEventDelete && window.confirm('Are you sure you want to delete this event?')) {
      onEventDelete(event.id);
    }
  };

  const handleConfirmClick =

  (e) => {
    e.stopPropagation();
    if (onConfirmResponsibility) {
      onConfirmResponsibility(event.id);
    }
  };

  // Format event time for display
  const getEventTimeDisplay =

  () => {
    const start = moment(event.start);
    const end = moment(event.end);

    if (event.allDay) {
      return 'All day';
    }

    if (start.isSame(end, 'day')) {
      return `${formatTime(start)} - ${formatTime(end)}`;
    } else {
      return `${formatDate(start, 'MMM D')} ${formatTime(start)} - ${formatDate(end, 'MMM D')} ${formatTime(end)}`;
    }
  };

  const renderEventContent =

  () => {
    const assigneeNames = getAssigneeNames();

    return (
      <div className="event-content">
        {/* Title row with avatars on the left */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          width: '100%'
        }}>
          {/* Show avatars to the left of the title */}
          {renderAssigneeAvatars()}
          
          <div className="event-title" title={event.title} style={{ flex: 1, minWidth: 0 }}>
            {event.title}
          </div>
        </div>
        
        {!isCompact &&
        <>
            <div className="event-time" style={{ opacity: 0.95 }}>
              {getEventTimeDisplay()}
            </div>
            
            {event.description &&
          <div className="event-description" title={event.description} style={{ opacity: 0.95 }}>
                {event.description}
              </div>}

          </>}

      </div>);

  };

  const renderEventActions =

  () => {
    if (isCompact) return null;

    return (
      <div className="event-actions" style={{ display: showTooltip ? 'flex' : 'none' }}>
        {onEventEdit && event.source !== 'ai-calendar' &&
        <button
          className="event-action-btn edit-btn"
          onClick={handleEditClick}
          title="Edit event">

            <AppIcon icon="pi pi-pencil" />
          </button>}

        
        {onEventDelete && event.source !== 'ai-calendar' &&
        <button
          className="event-action-btn delete-btn"
          onClick={handleDeleteClick}
          title="Delete event">

            <AppIcon icon="pi pi-trash" />
          </button>}

        
        {!event.confirmed && onConfirmResponsibility && isUserAssignee() &&
        <button
          className="event-action-btn confirm-btn"
          onClick={handleConfirmClick}
          title="Confirm responsibility">

            <AppIcon icon="pi pi-check" />
          </button>}

      </div>);

  };

  const renderTooltip =

  () => {
    if (!showTooltip || isCompact) return null;

    const assigneeNames = getAssigneeNames();

    return (
      <div className="event-tooltip">
        <div className="tooltip-title">{event.title}</div>
        <div className="tooltip-time">{getEventTimeDisplay()}</div>
        {assigneeNames ?
        <div className="tooltip-assignee" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Assigned to:</span>
            {renderAssigneeAvatars()}
            <span>{assigneeNames}</span>
          </div> :

        <div className="tooltip-assignee">Assigned to: N/A</div>}

        {event.description && <div className="tooltip-description">{event.description}</div>}
        {event.type && <div className="tooltip-type">Type: {event.type}</div>}
        {!event.confirmed && <div className="tooltip-status"><AppIcon icon="pi pi-exclamation-triangle" /> Unconfirmed</div>}
      </div>);

  };

  return (
    <div
      ref={eventRef}
      className={`calendar-event ${event.type || 'hard-deadline'} ${event.confirmed ? 'confirmed' : 'unconfirmed'}`}
      style={getEventStyles()}
      draggable={isDraggable}
      onClick={handleEventClick}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      title={isCompact ? `${event.title} (${getEventTimeDisplay()})` : undefined}>

      {renderEventContent()}
      {renderEventActions()}
      {renderTooltip()}
    </div>);

};

export default EventComponent;
